import type {
  CheckoutBrowserInfoScopeResponse,
  UpdateCheckoutBrowserInfoScopeRequest,
  BinaryExportOptions,
  ExportEnvelope,
  ExportOptions,
  JsonExportOptions,
  BulkCreateProcessorCostSchedulesRequest,
  CreateProcessorCostScheduleRequest,
  ProcessorCostEventsResponse,
  ProcessorCostScheduleResponse,
  ReplaceProcessorCostScheduleRequest,
  UpdateProcessorCostScheduleRequest,
  FeeStatementDetail,
  MerchantAccountResponse,
  MerchantAccountUpdateRequest,
  PaymentAttemptsListResponse,
  PaymentClientContextListResponse,
  PaymentResponse,
  PaymentsDeleteResponse,
  PaymentStatusHistoryResponse,
  ProfileResponse,
  RefundListResponse,
  SellToRestrictionsResponse,
  SettlementCurrentParams,
  SettlementCurrentResponse,
  SettlementOverviewParams,
  SettlementOverviewResponse,
  SettlementStatementListParams,
  SettlementStatementListResponse,
  StatementPdfParams,
  SubscriptionAnalyticsRequest,
  SubscriptionAnalyticsResponse,
  SubscriptionDrillRequest,
  ScopeDrillRequest,
} from '../../types';
import type {
  ReplayMaskingConfigResponse,
  UpdateReplayMaskingConfigRequest,
  ConnectorHealthConfig,
  AdminAttachVaultRequest,
  AdminAttachVaultResponse,
  AdminAttemptProcessorCost,
  AdminProcessorCostEventsParams,
  AdminVaultStateResponse,
  AdminCustomerListParams,
  AdminCustomerListResponse,
  AdminCustomerDetail,
  AdminTransactionExportParams,
  AdminTransactionExportRecord,
  AdminTransactionListParams,
  AdminTransactionListResponse,
  AdminUnresolvedRefundListParams,
  AdminUnresolvedRefundListResponse,
  AdminAnalyticsRequest,
  PlatformAnalyticsResponse,
  OverviewStatsResponse,
  PaymentAnalyticsRequest,
  PaymentAnalyticsResponse,
  AnalyticsScopeRequest,
  ClientAnalyticsRequest,
  DeviceDrillRequest,
  GeoDrillRequest,
  DrillResponse,
  DevicesAnalyticsResponse,
  GeoAnalyticsResponse,
  AnalyticsScopeResponse,
  AdminLedgerAnalyticsRequest,
  AdminLedgerAnalyticsResponse,
  AdminOrphanedBillingProfilesResponse,
  AdminCreateUserForMerchantRequest,
  AdminUpdateUserRequest,
  AdminUserResponse,
  PaymentAutoCloseConfigResponse,
  PaymentAutoCloseOverrideResponse,
  PromoConfigResponse,
  TransactionDeleteConfigResponse,
  TransactionDeleteOverrideResponse,
  UpdatePaymentAutoCloseConfigRequest,
  UpdatePaymentAutoCloseOverrideRequest,
  UpdatePromoConfigRequest,
  UpdateTransactionDeleteConfigRequest,
  UpdateTransactionDeleteOverrideRequest,
} from '../types';
import type { RequestExtras, RequestFn, RequestOptions } from '../../client';
import { runExport } from '../../exportDownload';

export class AdminPortal {
  constructor(private readonly request: RequestFn) {}

  async listCustomers(params?: AdminCustomerListParams): Promise<AdminCustomerListResponse> {
    return this.request('GET', '/admin-portal/customers', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  async getCustomer(customerId: string): Promise<AdminCustomerDetail> {
    return this.request('GET', `/admin-portal/customers/${encodeURIComponent(customerId)}`);
  }

  async listTransactions(
    params?: AdminTransactionListParams,
  ): Promise<AdminTransactionListResponse> {
    const query = transactionListQuery(params);
    return this.request('GET', '/admin-portal/transactions', {
      query,
    });
  }

  /**
   * The admin transaction search as a file. `GET /admin-portal/transactions/export`
   *
   * Takes the same filters as {@link AdminPortal.listTransactions}, sent the
   * same way, and returns the whole match set rather than a page — bounded at
   * 25,000 rows (500 for `pdf`) and refused past that with `DE_07` rather than
   * truncated. Every page of it is read inside one database snapshot, so a
   * payment updated while the file downloads is neither repeated nor skipped.
   *
   * `csv` and `json` are streamed and held to their `X-Delopay-Record-Count`;
   * see {@link ExportTransferOptions} for progress, cancellation and the
   * `EXPORT_INCOMPLETE` check. The format is negotiated by `Accept` and
   * defaults to `json`.
   */
  async exportTransactions(
    params?: AdminTransactionExportParams,
    options?: JsonExportOptions,
  ): Promise<ExportEnvelope<AdminTransactionExportRecord>>;
  async exportTransactions(
    params: AdminTransactionExportParams | undefined,
    options: BinaryExportOptions,
  ): Promise<Blob>;
  async exportTransactions(
    params: AdminTransactionExportParams | undefined,
    options: ExportOptions,
  ): Promise<ExportEnvelope<AdminTransactionExportRecord> | Blob>;
  async exportTransactions(
    params?: AdminTransactionExportParams,
    options?: ExportOptions,
  ): Promise<ExportEnvelope<AdminTransactionExportRecord> | Blob> {
    return runExport(
      this.request,
      'GET',
      '/admin-portal/transactions/export',
      { query: transactionListQuery(params) },
      options,
    );
  }

  /**
   * Full detail for a single transaction of ANY merchant — the same
   * `PaymentResponse` the merchant `payments.retrieve` returns. Admin-scoped:
   * the JWT (or admin API key) does not need to belong to the payment's
   * merchant; the backend resolves the owning merchant and reads it read-only
   * (no connector sync).
   */
  async getTransaction(paymentId: string): Promise<PaymentResponse> {
    return this.request('GET', `/admin-portal/transactions/${encodeURIComponent(paymentId)}`);
  }

  /**
   * Per-attempt history (retries across connectors, decline reasons) for a
   * single transaction of ANY merchant.
   */
  async getTransactionAttempts(paymentId: string): Promise<PaymentAttemptsListResponse> {
    return this.request(
      'GET',
      `/admin-portal/transactions/${encodeURIComponent(paymentId)}/attempts`,
    );
  }

  /**
   * Status timeline (intent / attempt / refund / dispute transitions) for a
   * single transaction of ANY merchant. `complete: false` marks timelines
   * partially reconstructed from current records.
   */
  async getTransactionStatusHistory(paymentId: string): Promise<PaymentStatusHistoryResponse> {
    return this.request(
      'GET',
      `/admin-portal/transactions/${encodeURIComponent(paymentId)}/status-history`,
    );
  }

  /**
   * Refunds for a single transaction of ANY merchant.
   */
  async getTransactionRefunds(paymentId: string): Promise<RefundListResponse> {
    return this.request(
      'GET',
      `/admin-portal/transactions/${encodeURIComponent(paymentId)}/refunds`,
    );
  }

  /**
   * Refunds of ANY merchant mirrored from a connector webhook at a
   * non-terminal status that no sync can advance. Newest first.
   */
  async listUnresolvedRefunds(
    params?: AdminUnresolvedRefundListParams,
  ): Promise<AdminUnresolvedRefundListResponse> {
    return this.request('GET', '/admin-portal/refunds/unresolved', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  async analytics(params: AdminAnalyticsRequest): Promise<PlatformAnalyticsResponse> {
    return this.request('GET', '/admin-portal/analytics', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  async overviewStats(): Promise<OverviewStatsResponse> {
    return this.request('GET', '/admin-portal/overview-stats');
  }

  async paymentAnalytics(params: PaymentAnalyticsRequest): Promise<PaymentAnalyticsResponse> {
    return this.request('GET', '/admin-portal/payment-analytics', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * One drill level of the analytics dashboard: the scope's daily series +
   * processor mix and its direct children's series. Range / metric / donut
   * toggles apply client-side; only a drill (passing merchant_id / project_id
   * / shop_id) fetches the next level.
   */
  async analyticsScope(params?: AnalyticsScopeRequest): Promise<AnalyticsScopeResponse> {
    return this.request('GET', '/admin-portal/analytics/scope', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Device analytics over the canonical client-context observation per
   * payment, rooted at all merchants and drillable via `merchant_id` /
   * `project_id` / `shop_id` like `analyticsScope`. Answers 200 with
   * `enabled: false` when the client-context optimisation-use switch is off.
   */
  async analyticsDevices(params?: ClientAnalyticsRequest): Promise<DevicesAnalyticsResponse> {
    return this.request('GET', '/admin-portal/analytics/devices', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Geo analytics over the canonical client-context observation per payment,
   * rooted at all merchants. `mode` selects the location claim (`ip` default,
   * `billing`).
   */
  async analyticsGeo(params?: ClientAnalyticsRequest): Promise<GeoAnalyticsResponse> {
    return this.request('GET', '/admin-portal/analytics/geo', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The recent payments behind one clicked geo target (map country/city or
   * local-hours heatmap cell), rooted at all merchants. Capped at 50 rows,
   * newest first, with the full match count alongside.
   */
  async analyticsGeoTransactions(params: GeoDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/admin-portal/analytics/geo/transactions', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The recent payments behind one clicked device target (browser/platform
   * family, device-model label or device class), rooted at all merchants.
   * 50 rows per page (`offset` for the next page), newest first.
   */
  async analyticsDeviceTransactions(params: DeviceDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/admin-portal/analytics/devices/transactions', {
      // A discriminated union carries no index signature, so the widening
      // goes via `unknown` — the union is the point.
      query: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The payments behind one clicked element of the admin payments dashboard
   * (processor / method slice, outcome segment, series bucket or breakdown
   * row), rooted at all merchants. Same contract as the merchant surface's
   * `analytics.scopeTransactions`. `GET /admin-portal/analytics/scope/transactions`
   */
  async analyticsScopeTransactions(params: ScopeDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/admin-portal/analytics/scope/transactions', {
      query: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Subscription analytics over `subscription` and `invoice`, rooted at all
   * merchants and drillable via `merchant_id` / `project_id` / `shop_id` like
   * `analyticsScope`. The `children` block at the root is the merchant
   * breakdown — there is no separate endpoint for it.
   *
   * Half the figures are **stocks** (a snapshot at the window's end, not a sum
   * over it), so `est_monthly_volume_usd` and `active` can match across a
   * 7-day and a 30-day window while `billed_volume_usd` does not. Day
   * granularity only.
   */
  async analyticsSubscriptions(
    params?: SubscriptionAnalyticsRequest,
  ): Promise<SubscriptionAnalyticsResponse> {
    return this.request('GET', '/admin-portal/analytics/subscriptions', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * The billing cycles behind one clicked element of the admin subscription
   * dashboard: an invoice outcome, a processor slice on either axis, a plan
   * row, a subscription status, a movement component, a series bucket or a
   * breakdown row. 50 rows per page (`offset` for the next), newest first,
   * with the full match count alongside.
   *
   * A cycle that never reached a payment is listed too — that is what "still
   * unpaid" means — and carries its invoice id as `payment_id` with
   * `invoice_id` set to the same value.
   */
  async analyticsSubscriptionsList(params: SubscriptionDrillRequest): Promise<DrillResponse> {
    return this.request('GET', '/admin-portal/analytics/subscriptions/list', {
      // A discriminated union carries no index signature, so the widening
      // goes via `unknown` — the union is the point, and the query builder
      // only ever reads own enumerable keys.
      query: params as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Platform billing dashboard: total balance across all ledger accounts (+
   * the net change), top-ups, fees collected, and the day-by-day ledger flow.
   * All amounts are in USD minor units.
   */
  async ledgerAnalytics(
    params?: AdminLedgerAnalyticsRequest,
  ): Promise<AdminLedgerAnalyticsResponse> {
    return this.request('GET', '/admin-portal/ledger-analytics', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }

  /**
   * Billing profiles no operator listing can reach, and the accounts that may
   * have taken one over.
   *
   * Deleting a merchant account leaves its billing profile — with its
   * balance, its Stripe customer and its saved card — behind, and the
   * Merchants listing enumerates merchant accounts, so such a profile is
   * absent from every operator surface rather than flagged on one. This reads
   * from the billing side instead, so it finds them.
   *
   * Both lists come back complete rather than paged: the healthy answer is two
   * empty arrays, and a truncated answer to *"is the set empty"* would read as
   * a clean bill of health.
   *
   * `adoption_candidates` are candidates, not findings — confirm a row against
   * the audit log before acting on it.
   */
  async orphanedBillingProfiles(): Promise<AdminOrphanedBillingProfilesResponse> {
    return this.request('GET', '/admin-portal/billing/orphaned-profiles');
  }

  /**
   * Retrieve a merchant account via the admin portal. Unlike
   * `merchantAccounts.retrieve`, this route accepts an admin JWT (or admin API
   * key) and does not require the JWT to be scoped to the target merchant.
   */
  async retrieveAccount(merchantId: string): Promise<MerchantAccountResponse> {
    return this.request('GET', `/admin-portal/accounts/${encodeURIComponent(merchantId)}`);
  }

  /**
   * Update a merchant account via the admin portal. Authenticated via admin JWT
   * or admin API key.
   */
  async updateAccount(
    merchantId: string,
    params: MerchantAccountUpdateRequest,
  ): Promise<MerchantAccountResponse> {
    return this.request('POST', `/admin-portal/accounts/${encodeURIComponent(merchantId)}`, {
      body: params,
    });
  }

  /**
   * Delete a merchant account via the admin portal. Authenticated via admin JWT
   * or admin API key.
   */
  async deleteAccount(merchantId: string): Promise<MerchantAccountResponse> {
    return this.request('DELETE', `/admin-portal/accounts/${encodeURIComponent(merchantId)}`);
  }

  /**
   * Create a brand-new user attached to the given merchant. The user is
   * marked `is_verified = true` so the admin can hand off credentials
   * immediately — no email round-trip is sent.
   */
  async createUserForMerchant(
    customerId: string,
    body: AdminCreateUserForMerchantRequest,
  ): Promise<AdminUserResponse> {
    return this.request('POST', `/admin-portal/customers/${encodeURIComponent(customerId)}/users`, {
      body,
    });
  }

  /**
   * Edit a user record. Any subset of fields may be supplied. `role_id`
   * requires `merchant_id`. `password` triggers a password reset (validated
   * against signup policy + JWT blacklist). `reset_2fa` clears TOTP state
   * so the user re-enrolls on next login. `is_active` supports both
   * directions: false soft-disables, true reactivates a soft-disabled user.
   *
   * Throws a `400` with `code === 'UR_66'` when the change would leave a
   * merchant with no administrator — `is_active: false` or `is_verified:
   * false` on, or a `role_id` demoting, the only active user whose role can
   * manage that merchant's users. Nothing is written. `error.data` carries a
   * {@link LastAdministratorRefusal} naming the merchant and which change was
   * refused; render that rather than the generic message.
   */
  async updateUser(userId: string, body: AdminUpdateUserRequest): Promise<AdminUserResponse> {
    return this.request('PATCH', `/admin-portal/users/${encodeURIComponent(userId)}`, { body });
  }

  /**
   * Soft-delete a user globally: deactivates the row, blacklists existing
   * JWTs, and wipes credentials. Distinct from `deleteUserRole`, which
   * removes a single role binding while leaving the user signed-in elsewhere.
   *
   * Throws the same `UR_66` as {@link updateUser} when the user is the last
   * administrator of any merchant they belong to, with `route: 'delete'`.
   */
  async deleteUser(userId: string): Promise<void> {
    return this.request('DELETE', `/admin-portal/users/${encodeURIComponent(userId)}`);
  }

  /**
   * Set a target merchant's shop iframe-allowed origins. Internal-admin
   * route — accepts an admin JWT or admin API key without requiring the
   * caller to be scoped to the target merchant. Pass `null` (or an empty
   * array) to clear the allowlist back to same-origin only.
   *
   * Server validates each origin: full origin (scheme + host[:port]),
   * no path/query/fragment, no wildcards.
   */
  async updateShopIframeOrigins(
    merchantId: string,
    profileId: string,
    origins: string[] | null,
  ): Promise<ProfileResponse> {
    return this.request(
      'POST',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/business_profile/${encodeURIComponent(profileId)}/iframe-origins`,
      { body: { iframe_allowed_origins: origins } },
    );
  }

  /**
   * Set (or clear) a target merchant shop's home country — the geo
   * dashboard's cross-border baseline. Internal-admin route. Pass `null` to
   * clear back to "international / no home country" (the default).
   *
   * `POST /admin-portal/accounts/{merchantId}/business-profile/{profileId}/home-country`
   */
  async updateShopHomeCountry(
    merchantId: string,
    profileId: string,
    homeCountry: string | null,
  ): Promise<ProfileResponse> {
    return this.request(
      'POST',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/business-profile/${encodeURIComponent(profileId)}/home-country`,
      { body: { home_country: homeCountry } },
    );
  }

  /**
   * One merchant's sell-to restrictions — the merchant-wide policy and every
   * shop's effective policy. Read-only; staff change a policy through the
   * merchant routes.
   *
   * `GET /admin-portal/accounts/{merchantId}/sell-to-restrictions`
   */
  async getSellToRestrictions(merchantId: string): Promise<SellToRestrictionsResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/sell-to-restrictions`,
    );
  }

  /** Admin setting for one merchant, independent of the merchant's own choice. */
  async getMerchantCheckoutBrowserInfo(
    merchantId: string,
  ): Promise<CheckoutBrowserInfoScopeResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/checkout-browser-info`,
    );
  }

  /** Replace the admin merchant setting. Any OFF remains final; changes are audited. */
  async updateMerchantCheckoutBrowserInfo(
    merchantId: string,
    body: UpdateCheckoutBrowserInfoScopeRequest,
  ): Promise<CheckoutBrowserInfoScopeResponse> {
    return this.request(
      'PUT',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/checkout-browser-info`,
      { body },
    );
  }

  /** Platform-wide policy for delivery of the five device fields to one connector. */
  async getConnectorCheckoutBrowserInfo(
    connector: string,
  ): Promise<CheckoutBrowserInfoScopeResponse> {
    return this.request(
      'GET',
      `/admin-portal/connectors/${encodeURIComponent(connector)}/checkout-browser-info`,
    );
  }

  /**
   * Store the connector policy. OFF withholds the fields for every merchant
   * only on backends with connector-delivery enforcement deployed. Earlier
   * backends store this setting without enforcing it; a successful save alone
   * is not evidence that delivery is blocked.
   */
  async updateConnectorCheckoutBrowserInfo(
    connector: string,
    body: UpdateCheckoutBrowserInfoScopeRequest,
  ): Promise<CheckoutBrowserInfoScopeResponse> {
    return this.request(
      'PUT',
      `/admin-portal/connectors/${encodeURIComponent(connector)}/checkout-browser-info`,
      { body },
    );
  }

  /**
   * The deployment's replay-masking rules: extra page elements the session
   * recorder hides on top of the ones it always hides.
   *
   * `GET /admin-portal/replay-masking`
   */
  async getReplayMasking(): Promise<ReplayMaskingConfigResponse> {
    return this.request('GET', '/admin-portal/replay-masking');
  }

  /**
   * Replace the deployment's replay-masking rules. Additive to the recorder's
   * built-in floor: a rule can widen what a recording leaves out and can never
   * reveal something the recorder hides unconditionally.
   *
   * `PUT /admin-portal/replay-masking`
   */
  async updateReplayMasking(
    params: UpdateReplayMaskingConfigRequest,
  ): Promise<ReplayMaskingConfigResponse> {
    return this.request('PUT', '/admin-portal/replay-masking', { body: params });
  }

  /**
   * One merchant's replay-masking rules.
   *
   * `GET /admin-portal/accounts/{merchantId}/replay-masking`
   */
  async getMerchantReplayMasking(merchantId: string): Promise<ReplayMaskingConfigResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/replay-masking`,
    );
  }

  /**
   * Replace one merchant's replay-masking rules.
   *
   * `PUT /admin-portal/accounts/{merchantId}/replay-masking`
   */
  async updateMerchantReplayMasking(
    merchantId: string,
    params: UpdateReplayMaskingConfigRequest,
  ): Promise<ReplayMaskingConfigResponse> {
    return this.request(
      'PUT',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/replay-masking`,
      { body: params },
    );
  }

  /**
   * One shop's replay-masking rules.
   *
   * `GET /admin-portal/accounts/{merchantId}/business-profile/{profileId}/replay-masking`
   */
  async getShopReplayMasking(
    merchantId: string,
    profileId: string,
  ): Promise<ReplayMaskingConfigResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/business-profile/${encodeURIComponent(profileId)}/replay-masking`,
    );
  }

  /**
   * Replace one shop's replay-masking rules.
   *
   * `PUT /admin-portal/accounts/{merchantId}/business-profile/{profileId}/replay-masking`
   */
  async updateShopReplayMasking(
    merchantId: string,
    profileId: string,
    params: UpdateReplayMaskingConfigRequest,
  ): Promise<ReplayMaskingConfigResponse> {
    return this.request(
      'PUT',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/business-profile/${encodeURIComponent(profileId)}/replay-masking`,
      { body: params },
    );
  }

  /**
   * Soft-delete a transaction of ANY merchant. Only payments whose status
   * is in the admin delete policy can be deleted; the action is audited.
   *
   * `DELETE /admin-portal/transactions/{paymentId}`
   */
  async deleteTransaction(paymentId: string): Promise<PaymentsDeleteResponse> {
    return this.request('DELETE', `/admin-portal/transactions/${encodeURIComponent(paymentId)}`);
  }

  /**
   * Recover (undelete) a soft-deleted transaction. Audited.
   *
   * `POST /admin-portal/transactions/{paymentId}/recover`
   */
  async recoverTransaction(paymentId: string): Promise<PaymentsDeleteResponse> {
    return this.request(
      'POST',
      `/admin-portal/transactions/${encodeURIComponent(paymentId)}/recover`,
    );
  }

  /**
   * Client/device observations captured while the buyer interacted with a
   * transaction of ANY merchant, oldest first.
   *
   * `GET /admin-portal/transactions/{paymentId}/client-context`
   */
  async getTransactionClientContext(paymentId: string): Promise<PaymentClientContextListResponse> {
    return this.request(
      'GET',
      `/admin-portal/transactions/${encodeURIComponent(paymentId)}/client-context`,
    );
  }

  /**
   * What the rail charged for one attempt of a transaction of ANY merchant,
   * off the attempt's settlement line. `line_recorded: false` means
   * no line exists for the attempt; an absent amount is "no figure", never
   * zero.
   *
   * `GET /admin-portal/transactions/{paymentId}/attempts/{attemptId}/processor-cost`
   */
  async getTransactionAttemptProcessorCost(
    paymentId: string,
    attemptId: string,
  ): Promise<AdminAttemptProcessorCost> {
    return this.request(
      'GET',
      `/admin-portal/transactions/${encodeURIComponent(paymentId)}/attempts/${encodeURIComponent(attemptId)}/processor-cost`,
    );
  }

  /** Read or refresh signed processor fees for a payment. Internal admin access required. */
  async getTransactionProcessorCostEvents(
    paymentId: string,
    params: AdminProcessorCostEventsParams = {},
  ): Promise<ProcessorCostEventsResponse> {
    return this.request(
      'GET',
      `/admin-portal/transactions/${encodeURIComponent(paymentId)}/processor-cost-events`,
      {
        query: {
          test_mode: params.test_mode,
          year: params.year,
          month: params.month,
          limit: params.limit,
          offset: params.offset,
          force_sync: params.force_sync,
        },
      },
    );
  }

  /**
   * DeloPay's own processor cost schedules — our acquirer contract, which
   * prices every merchant that has no schedule of its own, except an account
   * a shop owner brought (that rate is in their contract, not ours, and lives
   * on the merchant surface).
   *
   * Never returned on the merchant surface: these rates are DeloPay's cost
   * base. The rows carry no merchant.
   *
   * `GET /admin-portal/processor-costs`
   */
  async listProcessorCostSchedules(): Promise<ProcessorCostScheduleResponse[]> {
    return this.request('GET', '/admin-portal/processor-costs');
  }

  /**
   * Record a DeloPay-wide cost schedule — what a rail charges DeloPay, for
   * rails that report no fee on the payment itself. A cost derived from one
   * of these is marked `estimated` on the settlement line, never `reported`.
   *
   * `POST /admin-portal/processor-costs`
   */
  async createProcessorCostSchedule(
    params: CreateProcessorCostScheduleRequest,
  ): Promise<ProcessorCostScheduleResponse> {
    return this.request('POST', '/admin-portal/processor-costs', { body: params });
  }

  /**
   * Record several DeloPay-wide schedules at once, all or none — copy a price
   * list across methods. 1 to 200 entries; an invalid entry is answered `422`
   * naming its index and nothing is stored. The answer lists them in request
   * order.
   *
   * `POST /admin-portal/processor-costs/bulk`
   */
  async bulkCreateProcessorCostSchedules(
    params: BulkCreateProcessorCostSchedulesRequest,
  ): Promise<ProcessorCostScheduleResponse[]> {
    return this.request('POST', '/admin-portal/processor-costs/bulk', { body: params });
  }

  /**
   * Close a window, deactivate a DeloPay-wide schedule or annotate it. The
   * rate itself is not editable: it priced transactions that already
   * happened, and a new rate is a new row.
   *
   * `PUT /admin-portal/processor-costs/{scheduleId}`
   */
  async updateProcessorCostSchedule(
    scheduleId: string,
    params: UpdateProcessorCostScheduleRequest,
  ): Promise<ProcessorCostScheduleResponse> {
    return this.request('PUT', `/admin-portal/processor-costs/${encodeURIComponent(scheduleId)}`, {
      body: params,
    });
  }

  /**
   * Close a DeloPay-wide schedule at `effective_from` and open its successor
   * with the same scope and the same end, in one step: no transaction is
   * priced by both rates, and none falls between them. Answers the successor.
   *
   * `POST /admin-portal/processor-costs/{scheduleId}/replace`
   */
  async replaceProcessorCostSchedule(
    scheduleId: string,
    params: ReplaceProcessorCostScheduleRequest,
  ): Promise<ProcessorCostScheduleResponse> {
    return this.request(
      'POST',
      `/admin-portal/processor-costs/${encodeURIComponent(scheduleId)}/replace`,
      { body: params },
    );
  }

  /**
   * Delete a DeloPay-wide schedule. Restates nothing: every figure it derived
   * is already stamped on the settlement lines it priced.
   *
   * `DELETE /admin-portal/processor-costs/{scheduleId}`
   */
  async deleteProcessorCostSchedule(scheduleId: string): Promise<void> {
    return this.request(
      'DELETE',
      `/admin-portal/processor-costs/${encodeURIComponent(scheduleId)}`,
    );
  }

  /** Get the connector health polling interval. `GET /admin-portal/connector-health-config` */
  async getConnectorHealthConfig(): Promise<ConnectorHealthConfig> {
    return this.request('GET', '/admin-portal/connector-health-config');
  }

  /** Update the connector health polling interval. `PUT /admin-portal/connector-health-config` */
  async updateConnectorHealthConfig(params: {
    interval_minutes: number;
  }): Promise<ConnectorHealthConfig> {
    return this.request('PUT', '/admin-portal/connector-health-config', { body: params });
  }

  /**
   * The global payment auto-close policy.
   * `GET /admin-portal/auto-close-config`
   */
  async getAutoCloseConfig(): Promise<PaymentAutoCloseConfigResponse> {
    return this.request('GET', '/admin-portal/auto-close-config');
  }

  /**
   * Update the global payment auto-close policy. PATCH semantics — omitted
   * fields are left unchanged.
   *
   * `PUT /admin-portal/auto-close-config`
   */
  async updateAutoCloseConfig(
    params: UpdatePaymentAutoCloseConfigRequest,
  ): Promise<PaymentAutoCloseConfigResponse> {
    return this.request('PUT', '/admin-portal/auto-close-config', { body: params });
  }

  /**
   * One merchant's auto-close override plus the effective values.
   * `GET /admin-portal/accounts/{merchantId}/auto-close-config`
   */
  async getMerchantAutoCloseConfig(merchantId: string): Promise<PaymentAutoCloseOverrideResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/auto-close-config`,
    );
  }

  /**
   * Replace one merchant's auto-close override. REPLACE semantics — sending
   * both fields as `null` removes the override entirely.
   *
   * `PUT /admin-portal/accounts/{merchantId}/auto-close-config`
   */
  async updateMerchantAutoCloseConfig(
    merchantId: string,
    params: UpdatePaymentAutoCloseOverrideRequest,
  ): Promise<PaymentAutoCloseOverrideResponse> {
    return this.request(
      'PUT',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/auto-close-config`,
      { body: params },
    );
  }

  /**
   * The global transaction soft-delete policy (statuses admins may delete,
   * plus the deploy-time env ceiling).
   *
   * `GET /admin-portal/transaction-delete-config`
   */
  async getTransactionDeleteConfig(): Promise<TransactionDeleteConfigResponse> {
    return this.request('GET', '/admin-portal/transaction-delete-config');
  }

  /**
   * Replace the global deletable-status set. Must be a subset of the env
   * ceiling.
   *
   * `PUT /admin-portal/transaction-delete-config`
   */
  async updateTransactionDeleteConfig(
    params: UpdateTransactionDeleteConfigRequest,
  ): Promise<TransactionDeleteConfigResponse> {
    return this.request('PUT', '/admin-portal/transaction-delete-config', { body: params });
  }

  /**
   * One merchant's deletable-status override plus the effective set.
   * `GET /admin-portal/accounts/{merchantId}/transaction-delete-config`
   */
  async getMerchantTransactionDeleteConfig(
    merchantId: string,
  ): Promise<TransactionDeleteOverrideResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/transaction-delete-config`,
    );
  }

  /**
   * Replace one merchant's deletable-status override. `statuses: null`
   * removes the override; an empty list forbids deletion entirely.
   *
   * `PUT /admin-portal/accounts/{merchantId}/transaction-delete-config`
   */
  async updateMerchantTransactionDeleteConfig(
    merchantId: string,
    params: UpdateTransactionDeleteOverrideRequest,
  ): Promise<TransactionDeleteOverrideResponse> {
    return this.request(
      'PUT',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/transaction-delete-config`,
      { body: params },
    );
  }

  /**
   * A merchant's settlement statements. Same shapes as the merchant-facing
   * `settlement` resource, admin-authenticated.
   *
   * `GET /admin-portal/accounts/{merchantId}/settlement/statements`
   */
  async listSettlementStatements(
    merchantId: string,
    params: SettlementStatementListParams,
  ): Promise<SettlementStatementListResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/settlement/statements`,
      {
        query: {
          test_mode: params.test_mode,
          profile_id: params.profile_id,
          limit: params.limit,
          offset: params.offset,
        },
      },
    );
  }

  /**
   * One settlement statement with its breakdown.
   * `GET /admin-portal/accounts/{merchantId}/settlement/statements/{statementId}`
   */
  async getSettlementStatement(
    merchantId: string,
    statementId: string,
  ): Promise<FeeStatementDetail> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/settlement/statements/${encodeURIComponent(statementId)}`,
    );
  }

  /**
   * Export a settlement statement as PDF. Returns the raw bytes as a `Blob`
   * with the same auth and error handling as every other call. `options`
   * takes a signal, a timeout and an `onDownloadProgress` listener for the
   * download's bytes (the PDF carries a `Content-Length`, so `total` is set).
   *
   * `GET /admin-portal/accounts/{merchantId}/settlement/statements/{statementId}/pdf`
   */
  async downloadSettlementStatementPdf(
    merchantId: string,
    statementId: string,
    params?: StatementPdfParams,
    options?: RequestExtras,
  ): Promise<Blob> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/settlement/statements/${encodeURIComponent(statementId)}/pdf`,
      {
        query: {
          currency: params?.currency,
          include_transactions: params?.include_transactions,
        },
        responseType: 'blob',
        ...options,
      },
    );
  }

  /**
   * A merchant's per-shop settlement overview.
   * `GET /admin-portal/accounts/{merchantId}/settlement/overview`
   */
  async settlementOverview(
    merchantId: string,
    params: SettlementOverviewParams,
  ): Promise<SettlementOverviewResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/settlement/overview`,
      { query: { test_mode: params.test_mode } },
    );
  }

  /**
   * A merchant's live current-period settlement rollup.
   * `GET /admin-portal/accounts/{merchantId}/settlement/current`
   */
  async settlementCurrent(
    merchantId: string,
    params: SettlementCurrentParams,
  ): Promise<SettlementCurrentResponse> {
    return this.request(
      'GET',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/settlement/current`,
      { query: { test_mode: params.test_mode, profile_id: params.profile_id } },
    );
  }

  /**
   * A merchant's vault state: the attach entitlement, and the vault
   * configuration of every shop.
   *
   * `GET /admin-portal/accounts/{merchantId}/vault`
   */
  async getVaultState(merchantId: string): Promise<AdminVaultStateResponse> {
    return this.request('GET', `/admin-portal/accounts/${encodeURIComponent(merchantId)}/vault`);
  }

  /**
   * Attach a vault to one of a merchant's shops — creates the vault
   * connector account and points the profile at it in one request. The
   * Collect credentials are verified write-only before anything is stored.
   *
   * `POST /admin-portal/accounts/{merchantId}/vault/attach`
   */
  async attachVault(
    merchantId: string,
    params: AdminAttachVaultRequest,
  ): Promise<AdminAttachVaultResponse> {
    return this.request(
      'POST',
      `/admin-portal/accounts/${encodeURIComponent(merchantId)}/vault/attach`,
      { body: params },
    );
  }

  /**
   * Fetch the global welcome promotional-credit config (amount + message)
   * granted to newly created billing profiles. Internal-admin route.
   */
  async getPromoConfig(): Promise<PromoConfigResponse> {
    return this.request('GET', '/admin-portal/promo-config');
  }

  /**
   * Update the global welcome promotional-credit config. Any subset of
   * `amount` (minor units) / `message` may be supplied; omitted fields are
   * left unchanged. Returns the resulting config. Internal-admin route.
   */
  async updatePromoConfig(params: UpdatePromoConfigRequest): Promise<PromoConfigResponse> {
    return this.request('PUT', '/admin-portal/promo-config', { body: params });
  }
}

function transactionListQuery(params?: AdminTransactionListParams): RequestOptions['query'] {
  if (!params) return undefined;
  const {
    statuses,
    connectors,
    payment_methods,
    payment_method_types,
    currencies,
    merchant_connector_ids,
    connector_settlement_statuses,
    routing_approaches,
    ...query
  } = params;
  return {
    ...query,
    ...(statuses?.length ? { statuses: statuses.join(',') } : {}),
    ...(connectors?.length ? { connectors: connectors.join(',') } : {}),
    ...(payment_methods?.length ? { payment_methods: payment_methods.join(',') } : {}),
    ...(payment_method_types?.length
      ? { payment_method_types: payment_method_types.join(',') }
      : {}),
    ...(currencies?.length ? { currencies: currencies.join(',') } : {}),
    ...(merchant_connector_ids?.length
      ? { merchant_connector_ids: merchant_connector_ids.join(',') }
      : {}),
    ...(connector_settlement_statuses?.length
      ? { connector_settlement_statuses: connector_settlement_statuses.join(',') }
      : {}),
    ...(routing_approaches?.length ? { routing_approaches: routing_approaches.join(',') } : {}),
  };
}
